/** The My Bookings list's items. Every number is transcribed from the layout named in the
 *  comment — spec §2.18. Plain elements and inline styles: this renders in the preview
 *  frame and must not inherit the console's design system. */
import { imageUrl } from '../images';
import type { MyTripsItem } from '../mappers/myTrips';
import { cardMargins, marginStyle, SECTION_MARGINS } from './margins';

export interface MyTripsItemContext {
  imagesBase: string;
  imageWidthPx: number;
}

/** item_section.xml — minHeight 26dp, Legacy.Headline2; the past section's title is
 *  `Text.Style.Typeface.Bold` (synthetic bold over the Medium face). */
function Section({ text, bold }: { text: string; bold: boolean }) {
  return (
    <div
      className="and-type-legacy-headline2"
      data-and-item="section"
      style={{ ...marginStyle(SECTION_MARGINS), minHeight: 26, ...(bold ? { fontWeight: 700 } : {}) }}
    >
      {text}
    </div>
  );
}

const clamp = (lines: number) =>
  ({ display: '-webkit-box', WebkitLineClamp: lines, WebkitBoxOrient: 'vertical', overflow: 'hidden' }) as const;

/** item_activity_card_big.xml — CardView, white, radius 16dp, elevation 4dp; image
 *  184dp `centerCrop`; overline (Legacy.Overline.Blue, 1 line) 14dp under it with 16dp
 *  sides; title Legacy.Headline2, 2 lines (14dp top margin when there is no overline);
 *  description Body2.DarkMedium, 2 lines, 4dp above and 16dp below. `CardActivityItem.Big`
 *  margins: 8dp top and bottom, SINGLE → 16dp sides. A null image is the "coming soon"
 *  placeholder (`toUiKitImageWithDefaultConfig`). */
function CardBig({ item, ctx }: { item: Extract<MyTripsItem, { kind: 'cardBig' }>; ctx: MyTripsItemContext }) {
  const src = item.image ? imageUrl(ctx.imagesBase, item.image, ctx.imageWidthPx) : null;
  return (
    <div
      data-and-item="card-big"
      style={{
        ...marginStyle(cardMargins('SINGLE')),
        borderRadius: 16,
        overflow: 'hidden',
        background: 'var(--and-color-white)',
        boxShadow: '0 2px 4px rgb(0 0 0 / 0.16)',
      }}
    >
      {src ? (
        <img src={src} alt="" style={{ width: '100%', height: 184, objectFit: 'cover' }} />
      ) : (
        <div
          data-and-placeholder
          style={{
            height: 184,
            background: 'var(--and-color-surface)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--and-color-dark-disabled)',
            fontSize: 12,
          }}
        >
          Coming soon
        </div>
      )}
      {item.overline && (
        <div className="and-type-legacy-overline-blue" style={{ margin: '14px 16px 0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {item.overline}
        </div>
      )}
      <div
        className="and-type-legacy-headline2"
        style={{ margin: `${item.overline ? 0 : 14}px 16px ${item.description ? 0 : 16}px`, ...clamp(2) }}
      >
        {item.title}
      </div>
      {item.description && (
        <div className="and-type-body2-dark-medium" style={{ margin: '4px 16px 16px', whiteSpace: 'pre-line', ...clamp(2) }}>
          {item.description}
        </div>
      )}
    </div>
  );
}

export function MyTripsItemView({ item, ctx }: { item: MyTripsItem; ctx: MyTripsItemContext }) {
  return item.kind === 'section' ? <Section text={item.text} bold={item.bold} /> : <CardBig item={item} ctx={ctx} />;
}
